import React, { useEffect, useState } from "react";
import "./AddProductModal.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://agent-book-crm-backend.onrender.com";

const formConfig = {
  MAPD: [
    { name: "carrierId", label: "Carrier", type: "select", optionsSource: "carriers", required: true },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planId", label: "Plan ID (format AXXXX-XXX-X)", type: "text", pattern: "^[A-Za-z]{1}\\d{4}-\\d{3}-\\d{1}$", required: true },
    { name: "planType", label: "Plan Type", type: "select", options: ["PPO", "HMO", "HMO-POS", "DSNP", "CSNP", "PFFS"], required: true }
  ],
  "Individual Health": [
    { name: "carrierId", label: "Carrier", type: "select", optionsSource: "carriers", required: true },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planType", label: "Plan Type", type: "select", options: ["Marketplace Health", "Short-Term Health"], required: true }
  ],
  Life: [
    { name: "carrierId", label: "Carrier", type: "select", optionsSource: "carriers", required: true },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planType", label: "Plan Type", type: "select", options: ["Term Life", "Whole Life", "Accidental Death"], required: true }
  ],
  Dental: [
    { name: "carrierId", label: "Carrier", type: "select", optionsSource: "carriers", required: true },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planType", label: "Plan Type", type: "select", options: ["Dental Only", "Dental/Vision/Hearing"], required: true },
    { name: "benefitAmount", label: "Benefit Amount", type: "checkboxGroup", options: ["$1000", "$1500", "$2000", "$2500", "$3000", "$5000"], required: true }
  ],
  "Medicare Supplement": [
    { name: "carrierId", label: "Carrier", type: "select", optionsSource: "carriers", required: true },
    { name: "planName", label: "Plan Name", type: "checkboxGroup", options: ["Plan A", "Plan F", "Plan G", "Plan HG", "Plan N"], required: true }
  ],
  PDP: [
    { name: "carrierId", label: "Carrier", type: "select", optionsSource: "carriers", required: true },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planId", label: "Plan ID (format AXXXX-XXX-X)", type: "text", pattern: "^[A-Za-z]{1}\\d{4}-\\d{3}-\\d{1}$", required: true }
  ]
};

const AddProductModal = ({ coverageTypes, onClose, onSubmit }) => {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState("");
  const [formData, setFormData] = useState({});
  const [carriers, setCarriers] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchCarriers = async () => {
      const token = localStorage.getItem("token");

      try {
        const res = await fetch(`${API_BASE_URL}/api/carriers`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await res.json();

        if (Array.isArray(data)) {
          setCarriers(data);
        } else {
          console.error("Unexpected carriers format:", data);
          setCarriers([]);
        }
      } catch (err) {
        console.error("❌ Error fetching carriers:", err);
        setCarriers([]);
      }
    };

    fetchCarriers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => {
        const prevVals = Array.isArray(prev[name]) ? prev[name] : [];

        return {
          ...prev,
          [name]: checked
            ? [...prevVals, value]
            : prevVals.filter((v) => v !== value)
        };
      });

      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const normalizeSubmitValue = (fieldName, value) => {
    if (fieldName === "benefitAmount" && Array.isArray(value)) {
      return value.join(",");
    }

    if (fieldName === "planName" && Array.isArray(value)) {
      return value.join(",");
    }

    return value;
  };

  const handleSubmit = () => {
    setError("");

    const cfg = formConfig[selectedType] || [];

    for (const field of cfg) {
      const val = formData[field.name];

      if (field.required && (!val || (Array.isArray(val) && val.length === 0))) {
        setError(`Field "${field.label}" is required.`);
        return;
      }

      if (field.pattern && val && !(new RegExp(field.pattern).test(val))) {
        setError(`Field "${field.label}" does not match required format.`);
        return;
      }
    }

    const typeObj = coverageTypes.find((ct) => ct.name === selectedType);

    const payload = {
      coverageTypeId: typeObj?.id,
      carrierId: parseInt(formData.carrierId, 10),
      planName: normalizeSubmitValue("planName", formData.planName) || null,
      planId: formData.planId || null,
      planType: formData.planType || null,
      benefitAmount: normalizeSubmitValue("benefitAmount", formData.benefitAmount) || null
    };

    onSubmit(payload);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal add-product-modal" onClick={(e) => e.stopPropagation()}>
        {step === 1 ? (
          <>
            <h3>Select Coverage Type</h3>

            <select
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setFormData({});
                setError("");
              }}
            >
              <option value="">-- Select --</option>
              {coverageTypes.map((type) => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>

            <button
              className="modal-button save"
              onClick={() => setStep(2)}
              disabled={!selectedType}
            >
              Next
            </button>
          </>
        ) : (
          <>
            <h3>Add Product - {selectedType}</h3>

            {error && <p className="error">{error}</p>}

            {formConfig[selectedType]?.map((field) => (
              <div className="form-group" key={field.name}>
                <label>{field.label}</label>

                {field.type === "select" ? (
                  <select
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleInputChange}
                    required={field.required}
                  >
                    <option value="">Select</option>

                    {(field.optionsSource === "carriers"
                      ? carriers.map((carrier) => ({
                          label: carrier.name,
                          value: carrier.id
                        }))
                      : field.options.map((option) => ({
                          label: option,
                          value: option
                        }))
                    ).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === "checkboxGroup" ? (
                  <div className="checkbox-group">
                    {field.options.map((option) => (
                      <label key={option}>
                        <input
                          type="checkbox"
                          name={field.name}
                          value={option}
                          checked={formData[field.name]?.includes(option) || false}
                          onChange={handleInputChange}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                ) : (
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleInputChange}
                    required={field.required}
                    pattern={field.pattern || undefined}
                  />
                )}
              </div>
            ))}

            {!formConfig[selectedType] && (
              <p className="error">
                Products are not configured for this coverage type yet.
              </p>
            )}

            <div className="modal-actions">
              <button className="modal-button" onClick={() => setStep(1)}>
                Back
              </button>

              <button
                className="modal-button save"
                onClick={handleSubmit}
                disabled={!formConfig[selectedType]}
              >
                Submit
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddProductModal;